
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface JotformConfig {
  id?: string;
  api_key: string;
  form_ids: string[];
  is_eu_account: boolean;
  form_categories: Record<string, string>;
}

export const useJotformConfig = () => {
  const [config, setConfig] = useState<JotformConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStoring, setIsStoring] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch the user's Jotform configuration from Supabase
  const fetchConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('jotform_configs')
        .select('*')
        .eq('user_id', auth.user.id)
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (data) {
        // Convert form_categories from JSON to Record<string, string>
        let parsedCategories: Record<string, string> = {};
        if (typeof data.form_categories === 'object' && data.form_categories !== null) {
          parsedCategories = data.form_categories as Record<string, string>;
        } else if (typeof data.form_categories === 'string') {
          try {
            parsedCategories = JSON.parse(data.form_categories);
          } catch (e) {
            console.error('Error parsing form_categories:', e);
          }
        }

        setConfig({
          id: data.id,
          api_key: data.api_key,
          form_ids: data.form_ids,
          is_eu_account: data.is_eu_account,
          form_categories: parsedCategories,
        });

        // Also update localStorage for backward compatibility
        localStorage.setItem('jotformApiKey', data.api_key);
        localStorage.setItem('jotformFormIds', data.form_ids.join(','));
        localStorage.setItem('jotformIsEu', data.is_eu_account.toString());
        localStorage.setItem('jotformCategories', JSON.stringify(parsedCategories));
        
        // Alert-specific localStorage (for backward compatibility)
        localStorage.setItem('alertsJotformApiKey', data.api_key);
        localStorage.setItem('alertsJotformFormIds', data.form_ids.join(','));
        localStorage.setItem('alertsJotformIsEu', data.is_eu_account.toString());
        localStorage.setItem('alertsJotformCategories', JSON.stringify(parsedCategories));
      } else {
        // If no config in Supabase, try to get from localStorage for migration
        const localApiKey = localStorage.getItem('jotformApiKey') || localStorage.getItem('alertsJotformApiKey');
        const localFormIds = localStorage.getItem('jotformFormIds') || localStorage.getItem('alertsJotformFormIds');
        const localIsEu = localStorage.getItem('jotformIsEu') || localStorage.getItem('alertsJotformIsEu');
        const localCategories = localStorage.getItem('jotformCategories') || localStorage.getItem('alertsJotformCategories');

        if (localApiKey && localFormIds) {
          const configFromStorage: JotformConfig = {
            api_key: localApiKey,
            form_ids: localFormIds.split(',').map(id => id.trim()).filter(Boolean),
            is_eu_account: localIsEu === 'true',
            form_categories: localCategories ? JSON.parse(localCategories) : {},
          };
          
          setConfig(configFromStorage);
          
          // Immediately store the local config to Supabase
          await storeConfig(configFromStorage);
        } else {
          setConfig(null);
        }
      }
    } catch (err) {
      console.error('Error fetching Jotform config:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Store the Jotform configuration to Supabase
  const storeConfig = useCallback(async (newConfig: JotformConfig) => {
    try {
      setIsStoring(true);
      
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) {
        throw new Error('User not authenticated');
      }

      // Also update localStorage for backward compatibility
      localStorage.setItem('jotformApiKey', newConfig.api_key);
      localStorage.setItem('jotformFormIds', newConfig.form_ids.join(','));
      localStorage.setItem('jotformIsEu', newConfig.is_eu_account.toString());
      localStorage.setItem('jotformCategories', JSON.stringify(newConfig.form_categories));
      
      // Alert-specific localStorage (for backward compatibility)
      localStorage.setItem('alertsJotformApiKey', newConfig.api_key);
      localStorage.setItem('alertsJotformFormIds', newConfig.form_ids.join(','));
      localStorage.setItem('alertsJotformIsEu', newConfig.is_eu_account.toString());
      localStorage.setItem('alertsJotformCategories', JSON.stringify(newConfig.form_categories));

      if (config?.id) {
        // Update existing config
        const { error } = await supabase
          .from('jotform_configs')
          .update({
            api_key: newConfig.api_key,
            form_ids: newConfig.form_ids,
            is_eu_account: newConfig.is_eu_account,
            form_categories: newConfig.form_categories,
            updated_at: new Date().toISOString(),
          })
          .eq('id', config.id);

        if (error) throw error;
        
        toast.success('Jotform configuration updated');
      } else {
        // Insert new config
        const { error } = await supabase
          .from('jotform_configs')
          .insert({
            user_id: auth.user.id,
            api_key: newConfig.api_key,
            form_ids: newConfig.form_ids,
            is_eu_account: newConfig.is_eu_account,
            form_categories: newConfig.form_categories,
          });

        if (error) throw error;
        
        toast.success('Jotform configuration saved');
      }

      // Refresh the config
      await fetchConfig();
    } catch (err) {
      console.error('Error storing Jotform config:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      toast.error(`Failed to save configuration: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsStoring(false);
    }
  }, [config, fetchConfig]);

  // Delete the Jotform configuration
  const deleteConfig = useCallback(async () => {
    if (!config?.id) {
      toast.error('No configuration to delete');
      return;
    }

    try {
      setIsStoring(true);
      
      const { error } = await supabase
        .from('jotform_configs')
        .delete()
        .eq('id', config.id);

      if (error) throw error;

      // Clear localStorage as well
      localStorage.removeItem('jotformApiKey');
      localStorage.removeItem('jotformFormIds');
      localStorage.removeItem('jotformIsEu');
      localStorage.removeItem('jotformCategories');
      
      // Alert-specific localStorage
      localStorage.removeItem('alertsJotformApiKey');
      localStorage.removeItem('alertsJotformFormIds');
      localStorage.removeItem('alertsJotformIsEu');
      localStorage.removeItem('alertsJotformCategories');

      setConfig(null);
      toast.success('Jotform configuration deleted');
    } catch (err) {
      console.error('Error deleting Jotform config:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      toast.error(`Failed to delete configuration: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsStoring(false);
    }
  }, [config]);

  // Load config on initial mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    isLoading,
    isStoring,
    error,
    storeConfig,
    deleteConfig,
    fetchConfig
  };
};
